// pages/.../Users.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { FiPlusCircle } from 'react-icons/fi';
import { MdFileUpload } from 'react-icons/md';
import PortalDataTable from "../../../components/PortalDataTable";
import { SearchBox, Modal } from "../../../components";
import { userColumns } from "../../../utils";
import {
  useGetProfiledUsersPaged,
  useSearchProfiledUsers
} from '../../../hooks/users/useGet';
import ProfileUser from '../../../forms/create/ProfileUser';
import NetworkError from '../../../components/NetworkError';
import BulkProfileUsers from '../../../forms/create/BulkCreateUsers';

const Users = () => {
  // Single-user modal (existing)
  const [toggleModal, setToggleModal] = useState(false);
  const handleModalToggle = () => setToggleModal(prev => !prev);

  // Bulk modal (NEW)
  const [toggleBulkModal, setToggleBulkModal] = useState(false);
  const handleBulkModalToggle = () => setToggleBulkModal(prev => !prev);

  // pagination, fetching ... (unchanged)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const {
    data: basePage,
    isLoading: isBaseLoading,
    isError: isBaseError,
    error: baseError
  } = useGetProfiledUsersPaged(page, pageSize);

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      const q = query.trim();
      setDebouncedQuery(q.length >= 3 ? q : null);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => { setPage(1); }, [debouncedQuery]);

  const effectiveFilters = useMemo(() => {
    if (!debouncedQuery) return null;
    return { RequestValue: debouncedQuery, PageNumber: page, PageSize: pageSize };
  }, [debouncedQuery, page, pageSize]);

  const {
    data: searchPage,
    isLoading: isSearching,
    isError: isSearchError,
    error: searchError,
  } = useSearchProfiledUsers(effectiveFilters);

  const searchActive = !!effectiveFilters;

  const activePage = searchActive ? (searchPage ?? {}) : (basePage ?? {});
  const rows = activePage.items ?? [];
  const currentPage = activePage.pageNumber ?? page;
  const totalPages =
    typeof activePage.totalPages === 'number'
      ? activePage.totalPages
      : (activePage.hasNext ? currentPage + 1 : currentPage);
  const hasPrevious = activePage.hasPrevious ?? currentPage > 1;
  const hasNext = activePage.hasNext ?? (rows.length >= pageSize);
  const loading = searchActive ? isSearching : isBaseLoading;

  const countLabel = typeof activePage?.totalCount === 'number' ? activePage.totalCount : rows.length;

  if (isBaseError && baseError?.code === 'ERR_NETWORK') return <NetworkError error={baseError} />;
  if (isBaseError) return <span>Oops something happened....</span>;
  if (isSearchError) console.warn("Users search error:")//, searchError);

  const clearSearch = () => { setQuery(""); setPage(1); };
  const next = () => hasNext && setPage(p => p + 1);
  const prev = () => hasPrevious && setPage(p => Math.max(1, p - 1));
  const gotoPage = (p) => setPage(Math.max(1, p));
  const changePageSize = (sz) => { setPageSize(sz); setPage(1); };

  return (
    <div className="w-full flex flex-col px-4 sm:px-6 lg:px-10">
      <div className="branch_header">
        <div className="branch_header_title_group">
          <div className="branch_header_title_main">Users</div>
          {!loading && (
            <div className="branch_header_title_sub">
              {countLabel === 0 ? 'No ' : countLabel} users
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Bulk Profile (NEW) */}
          <button
            onClick={handleBulkModalToggle}
            className="branch_header_buttons_group bg-white border rounded text-[#0E86D4] px-3 py-2 flex items-center gap-2 hover:bg-gray-50"
            title="Bulk profile via Excel"
            type="button"
          >
            <MdFileUpload size={18} />
            Bulk Profile (Excel)
          </button>

          {/* Single Profile (existing) */}
          <button
            onClick={handleModalToggle}
            className="branch_header_buttons_group bg-[#0E86D4] rounded text-white px-3 py-2 flex items-center gap-2 hover:bg-[#0d78bd]"
            type="button"
            title="Profile User"
          >
            <FiPlusCircle />
            Profile User
          </button>
        </div>
      </div>

      <SearchBox
        placeholder="Search users (name / email / phone)"
        value={query}
        onChange={setQuery}
      />

      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm text-gray-600">Page size:</span>
        <select
          className="border rounded px-2 py-1 text-sm"
          value={pageSize}
          onChange={(e) => changePageSize(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((sz) => (
            <option key={sz} value={sz}>{sz}</option>
          ))}
        </select>
        <button
          className="ml-auto text-sm underline text-blue-600"
          onClick={clearSearch}
          type="button"
          title="Clear search"
        >
          Clear search
        </button>
      </div>

      {/* Modals */}
      <Modal
        title='Profile User'
        form={<ProfileUser onClose={handleModalToggle} />}
        toggleModal={toggleModal}
        handleToggle={handleModalToggle}
      />

      <Modal
        title='Bulk Profile Users'
        form={<BulkProfileUsers onClose={handleBulkModalToggle} />}
        toggleModal={toggleBulkModal}
        handleToggle={handleBulkModalToggle}
      />

      <PortalDataTable
        cols={userColumns}
        loading={loading}
        rows={rows}
        currentPage={currentPage}
        totalPages={totalPages}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        next={next}
        previous={prev}
        onPageChange={gotoPage}
      />
    </div>
  );
};

export default Users;
